"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Search, Loader2 } from "lucide-react";
import { ControlOwner } from "../types";

interface ControlOwnerSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (owners: ControlOwner[]) => void;
  existingOwners?: ControlOwner[];
}

const ControlOwnerSelectorDialogComponent: React.FC<ControlOwnerSelectorDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  existingOwners = [],
}) => {
  const [controlOwners, setControlOwners] = useState<Record<string, any>>({});
  const [selectedOwners, setSelectedOwners] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const existingOwnersRef = useRef<ControlOwner[]>([]);
  const prevOpenRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const onSelectRef = useRef(onSelect);

  // Stabilize refs to prevent callback recreation issues
  onCloseRef.current = onClose;
  onSelectRef.current = onSelect;

  // Memoize existingOwners IDs to detect actual changes
  const existingOwnersIds = useMemo(() => {
    return existingOwners.map(o => o.id).join(',');
  }, [existingOwners]);

  // Update ref only when the array actually changes
  useEffect(() => {
    const currentIds = existingOwnersRef.current.map(o => o.id).join(',');
    if (currentIds !== existingOwnersIds) {
      existingOwnersRef.current = existingOwners;
    }
  }, [existingOwners, existingOwnersIds]);

  // Initialize selected owners only when the dialog opens. Defer to next tick to avoid
  // "Maximum update depth" with Radix Dialog's setRef running in the same commit.
  useEffect(() => {
    if (isOpen) {
      if (!prevOpenRef.current) {
        // Use requestAnimationFrame for better timing with Radix UI ref management
        let timeoutId: NodeJS.Timeout | undefined;
        const rafId = requestAnimationFrame(() => {
          timeoutId = setTimeout(() => {
            const owners = existingOwnersRef.current;
            if (owners && owners.length > 0) {
              setSelectedOwners(new Set(owners.map((o: ControlOwner) => o.id)));
            } else {
              setSelectedOwners(new Set());
            }
            prevOpenRef.current = true;
          }, 0);
        });
        return () => {
          cancelAnimationFrame(rafId);
          if (timeoutId) clearTimeout(timeoutId);
        };
      }
    } else {
      prevOpenRef.current = false;
      setSearchTerm("");
    }
  }, [isOpen]);

  const loadControlOwners = useCallback(async () => {
    setLoading(true);
    try {
      if (window.sharePointAPI && typeof window.sharePointAPI.downloadControlOwnersJson === 'function') {
        const result = await window.sharePointAPI.downloadControlOwnersJson();
        
        if (result.success && result.data) {
          setControlOwners(result.data.controlOwners || {});
        } else {
          setControlOwners({});
        }
      } else {
        setControlOwners({});
      }
    } catch (error) {
      console.error('Error loading control owners:', error);
      setControlOwners({});
    } finally {
      setLoading(false);
    }
  }, []);

  // Load control owners from Libraries_ControlOwners.json (SharePoint juggernaut folder) when dialog opens.
  // Defer to next tick to avoid update depth issues with Radix Dialog ref management.
  useEffect(() => {
    if (!isOpen) return;
    let timeoutId: NodeJS.Timeout;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        loadControlOwners();
      }, 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen, loadControlOwners]);

  const handleToggleOwner = (ownerId: string) => {
    setSelectedOwners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerId)) {
        newSet.delete(ownerId);
      } else {
        newSet.add(ownerId);
      }
      return newSet;
    });
  };

  // Helper function to extract string from textResponse object or string
  const extractString = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'plainText' in value) {
      return value.plainText || value.html || '';
    }
    return '';
  };

  const handleConfirm = useCallback(() => {
    const selected = Array.from(selectedOwners)
      .map(ownerId => {
        const ownerData = controlOwners[ownerId];
        if (!ownerData) return null;
        
        return {
          id: ownerId,
          name: extractString(ownerData.name) || "",
          jobTitle: extractString(ownerData.designation || ownerData.jobTitle) || "",
          background: extractString(ownerData.authorityCompetence || ownerData.background) || "",
          email: extractString(ownerData.emailid || ownerData.email) || "",
        };
      })
      .filter((owner): owner is ControlOwner => owner !== null);
    
    onSelectRef.current(selected);
    onCloseRef.current();
  }, [selectedOwners, controlOwners]);

  const handleCancel = useCallback(() => {
    setSearchTerm("");
    onCloseRef.current();
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      handleCancel();
    }
  }, [handleCancel]);

  // Filter control owners based on search term
  const filteredOwners = Object.entries(controlOwners).filter(([id, owner]) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    const name = extractString(owner.name).toLowerCase();
    const designation = extractString(owner.designation || owner.jobTitle).toLowerCase();
    return name.includes(search) || designation.includes(search);
  });

  // Only mount Dialog when open to avoid Radix setRef update loops (maximum update depth)
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-black/90 border-white/10 text-white max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white">Select Control Owners</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              type="text"
              placeholder="Search control owners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-black/40 border-white/20 text-white placeholder:text-white/40"
            />
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-white/60" />
              <span className="ml-2 text-white/60">Loading control owners...</span>
            </div>
          ) : filteredOwners.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-white/60">
              {searchTerm ? "No control owners found matching your search." : "No control owners available."}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto border border-white/10 rounded-lg">
              <div className="p-2 space-y-2">
                {filteredOwners.map(([ownerId, ownerData]) => {
                  const isSelected = selectedOwners.has(ownerId);
                  return (
                    <label
                      key={ownerId}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer border border-transparent hover:border-white/10 transition-colors"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleToggleOwner(ownerId)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-white">
                          {extractString(ownerData.name) || "Unnamed Owner"}
                        </div>
                        <div className="text-sm text-white/60 mt-1">
                          {extractString(ownerData.designation || ownerData.jobTitle) || "No designation"}
                        </div>
                        {extractString(ownerData.authorityCompetence) && (
                          <div className="text-xs text-white/40 mt-1 line-clamp-2">
                            {extractString(ownerData.authorityCompetence)}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Selection Count */}
          {selectedOwners.size > 0 && (
            <div className="text-sm text-white/60">
              {selectedOwners.size} control owner{selectedOwners.size !== 1 ? 's' : ''} selected
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            className="border-white/10 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            className="bg-primary text-white hover:bg-primary/80"
            disabled={selectedOwners.size === 0}
          >
            Confirm ({selectedOwners.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const ControlOwnerSelectorDialog = React.memo(ControlOwnerSelectorDialogComponent);

